<template>
  <div class="d-flex flex-column parent-container">
    <div class="nav-bar-layout-container">
      <TaazeNavbar />
    </div>
    <div class="flex-grow-1 layout-container chat-container">
      <div class="d-flex flex-column">
        <div class="chat-header chat-content align-self-center">Talk to the Books <div class="chat-sub-header">{{ VERSION }} 版
          </div>
        </div>
        <div v-for="(chat, index) in chatHistory" :key="index" :chat-index="index">
          <ChatMessage :chat="chat" :is-prompting-enabled="isPromptingEnabled()"
            :is-share-enabled="isSharingEnabled(index, chat)"
            @on-prompt-suggestion-selected="onPromptSuggestionSelected" @on-share-click="onChatShare" />
        </div>
        <ComingSoonMessage v-if="isTrialOver()" />

      </div>
    </div>
    <div v-if="product != null" class="product-card-chat-container layout-container">
      <ChatInput v-model="chatInputValue" :readonly="isTrialUser" :is-chat-enabled="true" :is-focused="isChatFocused"
        :border-color="chatBorderColor" :icon-color="chatIconColor" :placeholder-color="chatPlaceholderColor"
        @on-message-send="onMessageSend" @on-focused="onChatFocused" @on-out-of-focus="onChatOutOfFocus" />

      <transition name="slide-fade">
        <ProductCard v-if="!isUserTyping" :product="product" />
      </transition>
    </div>
  </div>

  <InfoModalComponent ref="InfoModalComponent" header="您尚未解鎖聊天提問功能" body="立即購買單筆訂單滿 $699 或購買任 1 本電子書 / 電子雜誌即可解鎖聊天提問功能"
    button-text="立即購物" @on-button-click="onModalButtonClick" />

</template>

<script setup lang="ts">
import { ref, nextTick, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router'
import { TalkToTheBookAIServiceV2 } from '@/services/TalkToTheBookAIServiceV2';
import { AuthorizationService } from '@/services/AuthorizationService';
import { SharedResponseService } from '@/services/SharedResponseService';
import { SocialMediaShareService } from '@/services/SocialMediaShareService';
import { TaazeProductUrlCreator } from '@/utils/taazeProductUrlCreator';
import { scrollToBottom, scrollTo } from '@/utils/scrollUtils';
import { v4 as uuidv4 } from 'uuid';

import { TAAZE_WEBSITE, OCOPY_WELCOME_MESSAGE, SHARE_RESPONSE_MESSAGE } from '@/assets/strings';
import { TAAZE_MAIN_COLOR } from '@/assets/colors';

import ProductCard from '@/components/ProductCard.vue';
import ChatInput from '@/components/ui/ChatInput.vue';
import TaazeNavbar from '@/components/layout/TaazeNavbar.vue';
import ChatMessage from '@/components/ChatMessage.vue'
import ComingSoonMessage from '@/components/ComingSoonMessage.vue';
import InfoModalComponent from '@/components/ui/InfoModalComponent.vue';
import Chat from '@/interfaces/Chat';
import Product from '@/models/Product';
import ProductRepository from '@/repositories/ProductRepository';
import TalkToTheBookSource from '@/interfaces/TalkToTheBookSource';

const DISABLED_COLOR = '#E0E0E0';
const BASE_PROMPT = '請幫我總結這本書';
const VERSION = process.env.VUE_APP_VERSION;

const productId: string | undefined = useRoute().query.product_id?.toString();
const userId: string | undefined = useRoute().query.user_id?.toString();
const firstQuestionIndex = Number(useRoute().query.q ?? 0);
const promptLimit = 6;

const talkToTheBookAIService = new TalkToTheBookAIServiceV2(process.env.VUE_APP_TALK_TO_THE_BOOK_API_URL!);
const authorizationService = new AuthorizationService(process.env.VUE_APP_TALK_TO_THE_BOOK_API_URL!);
const sharedResponseService = new SharedResponseService(process.env.VUE_APP_TALK_TO_THE_BOOK_API_URL!);
const socialMediaShareService = new SocialMediaShareService();
const taazeProductUrlCreator = new TaazeProductUrlCreator();
const productRepository = new ProductRepository(process.env.VUE_APP_TAAZE_API_URL!);

const chatHistory = ref<Chat[]>([]);
const modalComponent = ref<InstanceType<typeof InfoModalComponent> | null>(null);
const chatInputValue = ref('');
const onPrompting = ref(false);
const isChatFocused = ref(false);
const isUserTyping = computed(() => chatInputValue.value !== '' || isChatFocused.value);
const chatBorderColor = computed(() => (chatInputValue.value !== '' && !isChatFocused.value) || onPrompting.value ? DISABLED_COLOR : TAAZE_MAIN_COLOR);
const chatIconColor = computed(() => onPrompting.value ? DISABLED_COLOR : TAAZE_MAIN_COLOR);
const chatPlaceholderColor = computed(() => onPrompting.value ? DISABLED_COLOR : TAAZE_MAIN_COLOR);

let sessionId = '';
let product: Product | null = null;
let isTrialUser = true;
let predefinedQuestions: { [key: string]: string } | null = null;
let promptCounter = 0;
let lastUserChatHeight = 0;
let streamBuffer = '';
let contentTimer: ReturnType<typeof setTimeout> | number;

onMounted(async () => {
  await setup();
});

async function setup() {
  if (productId === undefined || userId === undefined) {
    return;
  }

  sessionId = await authorizationService.startSession(userId);
  product = await productRepository.getProductById(productId);
  isTrialUser = !await authorizationService.authorize(userId, sessionId);
  predefinedQuestions = await talkToTheBookAIService.getPredefinedQuestions();

  addNewChat('bot', OCOPY_WELCOME_MESSAGE, [], false, false);

  let firstQuestion = predefinedQuestions[firstQuestionIndex] ?? BASE_PROMPT;
  addNewChat('user', firstQuestion, [], true, true);

  talkToTheBookAIService.prompt(product.id, firstQuestion, userId, sessionId, onContentStreamed);
  addNewChat();

  window.addEventListener("beforeunload", () => {
    authorizationService.sessionEnd(sessionId, true);
  });
}

function onContentStreamed(content: string | null, promptSuggestions: string | null, sources: TalkToTheBookSource[]) {
  if (chatHistory.value.length === 2) {
    content = '';
  }

  const lastBotChat = chatHistory.value.findLast(chat => chat.type === 'bot');

  if (lastBotChat) {
    lastBotChat.isLoading = false;

    // Append content to a buffer and start the timer if not already started
    streamBuffer += (content === null) ? '' : content;

    if (contentTimer) {
      clearTimeout(contentTimer);
    }

    // Function to output the content smoothly with timing control
    const outputContent = () => {
      if (streamBuffer.length > 0) {
        const charsToAdd = streamBuffer.slice(0, 5);
        lastBotChat.content += charsToAdd;
        lastBotChat.content = lastBotChat.content.replace(/\[(\d+)\]/g, '');
        streamBuffer = streamBuffer.slice(5);
        contentTimer = setTimeout(outputContent, 7);
      } else if (promptSuggestions !== null) {
        onStreamComplete(promptSuggestions, sources);
        return;
      }

      scrollToLastChat();
    };

    // Start the output process with a delay before adding the first character
    contentTimer = setTimeout(outputContent, 7);
  }
}

function onStreamComplete(promptSuggestions: string, sources: TalkToTheBookSource[]) {
  promptCounter++;

  sources.sort((a, b) => {
    return b.prod_title_main.length - a.prod_title_main.length;
  });

  if (!isTrialOver()) {
    if (chatHistory.value.length === 2) {
      chatHistory.value.pop();
    }

    const lastBotMessage = chatHistory.value[chatHistory.value.length - 1];
    lastBotMessage.suggestions = promptSuggestions.split('\n').filter(suggestion => suggestion.trim() !== '');
    lastBotMessage.isGenerating = false;

    //Adding hyperlinks to the book names if it have a source.
    for (let i = 0; i < sources.length; i++) {
      const source = sources[i];
      const link = taazeProductUrlCreator.createProductUrl(source.prod_id);
      const regex = new RegExp(source.prod_title_main, 'g');
      lastBotMessage.content = lastBotMessage.content.replace(regex, `[${i}](${link})`);
    }

    for (let i = 0; i < sources.length; i++) {
      const source = sources[i];
      lastBotMessage.content = lastBotMessage.content.replace(new RegExp(`\\[${i}\\]`, 'g'), `[${source.prod_title_main}]`);
    }
  }

  onPrompting.value = false;

  scrollToLastChat();
}

function prompt(prompt: string) {
  if (product === null || userId === undefined) {
    return;
  }

  onPrompting.value = true;

  addNewChat('user', prompt.replace(/^\d+\.\s/, ''), [], true);

  talkToTheBookAIService.prompt(product.id, prompt, userId, sessionId, onContentStreamed);
  addNewChat();

  scrollToChatBottom(true);
}

function onMessageSend() {
  if (!onPrompting.value && chatInputValue.value !== '') {
    prompt(chatInputValue.value);
    resetChatInputState();
  }
}

function onPromptSuggestionSelected(suggestion: string) {
  if (!onPrompting.value) {
    prompt(suggestion);
  }
}

async function onChatShare(platform: 'facebook' | 'line', chat: Chat) {
  if (productId === undefined || userId === undefined) {
    return;
  }

  let previousChat: Chat | undefined = undefined;
  for (let i = 1; i < chatHistory.value.length; i++) {
    if (chatHistory.value[i].id === chat.id) {
      previousChat = chatHistory.value[i - 1];
      break;
    }
  }

  if (previousChat === undefined) {
    return;
  }

  const shareUrl = await sharedResponseService.createShareLink(productId, userId, previousChat.content, chat.content);
  const productUrl = taazeProductUrlCreator.createProductUrl(productId);

  const shareMessage = `${product?.name}，${SHARE_RESPONSE_MESSAGE}\n\n${shareUrl}`;

  if (platform === 'line') {
    const share_url = socialMediaShareService.getLineShareUrl(productUrl, shareMessage);
    window.open(share_url, '_blank');
  }

  if (platform === 'facebook') {
    const share_url = socialMediaShareService.getFacebookShareUrl(shareUrl, shareMessage);
    window.open(share_url, '_blank');
  }
}

function isPromptingEnabled() {
  return !onPrompting.value && !isTrialOver();
}

function isSharingEnabled(chatIndex: number, chat: Chat) {
  return (chatIndex > 0 && chat.type !== 'user' && chat.isLoading === false && chat.isGenerating === false);
}

function isTrialOver() {
  return isTrialUser && promptCounter >= promptLimit;
}

function onChatFocused() {
  if (isTrialUser) {
    modalComponent.value?.show();
    return;
  }

  isChatFocused.value = true;

  const scrollableDiv = document.getElementsByClassName('chat-container')[0] as HTMLElement;
  const isAtBottom = scrollableDiv.scrollHeight - scrollableDiv.scrollTop <= scrollableDiv.clientHeight + 10;

  if (!isAtBottom && chatInputValue.value === '') {
    scrollTo(scrollableDiv, scrollableDiv.scrollTop - 130, 'instant', 0);
  }
}

function onChatOutOfFocus() {
  isChatFocused.value = false;

  const scrollableDiv = document.getElementsByClassName('chat-container')[0];
  const isAtBottom = scrollableDiv.scrollHeight - scrollableDiv.scrollTop <= scrollableDiv.clientHeight + 10;

  if (!isAtBottom && !isUserTyping.value) {
    nextTick(() => {
      scrollableDiv.scrollTop += 130;
    });

    return;
  }
}

function onModalButtonClick() {
  window.open(TAAZE_WEBSITE, '_blank');
}

function calculateLastUserChatHeight() {
  if (chatHistory.value.length === 0) {
    return 0;
  }

  nextTick(() => {
    lastUserChatHeight = 0;

    const lastUserChatIndex = chatHistory.value.findLastIndex(chat => chat.type === 'user');

    for (let index = 0; index < chatHistory.value.length; index++) {
      if (index > lastUserChatIndex) {
        break;
      }

      const chatElement = document.querySelector(`[chat-index="${index}"]`) as HTMLElement;
      lastUserChatHeight += chatElement.clientHeight;
    }
  });
}

function addNewChat(userType: 'user' | 'bot' = 'bot', content = '', suggestions = [], isLoading = true, isGenerating = true) {
  chatHistory.value.push(
    {
      id: uuidv4(),
      type: userType,
      content: content,
      suggestions: suggestions,
      isLoading: isLoading,
      isGenerating: isGenerating
    }
  );

  if (userType === 'user') {
    calculateLastUserChatHeight();
  }
}

function scrollToChatBottom(forced = false) {
  const chatBox = document.getElementsByClassName('chat-container')[0] as HTMLElement;
  scrollToBottom(chatBox, forced);
}

function scrollToLastChat() {
  const chatBox = document.getElementsByClassName('chat-container')[0] as HTMLElement;
  const isAtBottom = chatBox.scrollHeight - chatBox.scrollTop <= chatBox.clientHeight + 50;

  const lastUserChatIndex = chatHistory.value.findLastIndex(chat => chat.type === 'user');
  const chatElement = document.querySelector(`[chat-index="${lastUserChatIndex}"]`) as HTMLElement;
  const chatElementClientRect = chatElement.getBoundingClientRect();

  // Only scroll if the chat box is at the bottom and the last user chat is visible
  if (isAtBottom && chatElementClientRect.y > 0) {
    scrollTo(chatBox, lastUserChatHeight, 'smooth', 50);
  }
}

function resetChatInputState() {
  chatInputValue.value = '';
  isChatFocused.value = false;
}
</script>

<style scoped>
.parent-container {
  padding: 0;
  margin: 0;
  height: 100dvh;
  background-color: #F5EEE6;
}

.layout-container {
  padding-left: 16px;
  padding-right: 16px;
}

.chat-container {
  padding-top: 16px;
  padding-bottom: 16px;
  overflow: auto;
}

.chat-container::-webkit-scrollbar {
  width: 12px;
  height: 12px;
}

.chat-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.chat-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.chat-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.chat-header {
  font-family: 'Noto Sans TC', sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: #68444A;
}

.chat-content {
  margin-bottom: 16px;
  gap: 16px;
}

.chat-sub-header {
  display: inline;
  font-family: 'Noto Sans TC', sans-serif;
  font-size: 14px;
  font-weight: 400;
}

.product-card-chat-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: white;
  padding-top: 16px;
  padding-bottom: 16px;
}

@media (min-width: 768px) {
  .layout-container {
    padding-left: 78px;
    padding-right: 78px;
  }
}

@media (min-width: 1200px) {
  .layout-container {
    padding-left: 100px;
    padding-right: 100px;
  }

  .nav-bar-layout-container {
    padding-left: 100px;
    padding-right: 100px;
  }
}

.slide-fade-enter-active {
  transition: all 0.6s ease-out;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}
</style>